import { ChangeDetectorRef, Component, EventEmitter, Inject, inject, Input, OnInit, Optional, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Condition, ConditionExpression, decodeHTML, ExpressionType, ICondition, Operators } from '../../filter';
import { FilterProvider } from '../../interfaces/filter-provider.interface';
import { ProjectProvider } from '../../interfaces/project-provider.interface';
import { FILTER_PROVIDER, PROJECT_PROVIDER, FILTER_VALUES } from '../../tokens';
import { BoundsSelection, ProjectPointSelection } from '../../../map/project/project-points.service';
import { StorageService } from '../../../services/storage/storage.service';

export type FilterScope = 'visible' | 'all';

@Component({
	standalone: true,
	selector: 'wd-filter',
	templateUrl: './filter.component.html',
	styleUrls: ['./filter.component.css'],
	imports: [CommonModule, FormsModule]
})
export class FilterComponent implements OnInit {
	activeModal = inject(NgbActiveModal);

	@Input() local!: boolean;
	@Input() usedFiltersOverride?: Set<string>;
	@Input() scope: FilterScope = 'visible';
	@Input() showScopeSwitch = false;

	@Output() scopeChanged = new EventEmitter<FilterScope>();
	@Output() filterApplied = new EventEmitter<string>();

	name?: string;
	conditions?: ConditionExpression;
	filters?: string[];

	newFilterName?: string;
	showNewFilterInput = false;
	newCondition: ICondition = Condition.emptyCondition();
	newArgs: any[] = [];

	ExpressionType = ExpressionType;
	Operators = Operators;
	Values: any;
	keysOf = Object.keys;

	private usedFilters = new Set<string>();

	constructor(
		@Inject(FILTER_PROVIDER) private fs: FilterProvider,
		@Inject(FILTER_VALUES) filterValues: any,
		private storage: StorageService,
		@Inject(PROJECT_PROVIDER) @Optional() private ps: ProjectProvider,
		private cdr: ChangeDetectorRef
	) {
		this.Values = filterValues;
		const update = () => this.fs.list_keys().then((filters: string[]) => this.updateFilters(filters));
		this.fs.filtersUpdated.subscribe(() => update());
		update();
	}

	async ngOnInit(): Promise<void> {
		await this.loadUsedFilters();
	}

	private async loadUsedFilters(): Promise<void> {
		this.usedFilters.clear();

		if (this.usedFiltersOverride) {
			this.usedFiltersOverride.forEach(f => this.usedFilters.add(f));
			this.cdr.detectChanges();
			return;
		}

		if (!this.ps || !this.ps.project()) {
			this.cdr.detectChanges();
			return;
		}

		try {
			const selection = await this.ps.getProjectValue('projectPoints') as ProjectPointSelection | undefined;
			if (!selection) {
				this.cdr.detectChanges();
				return;
			}

			if (selection.globalFilterName) {
				this.usedFilters.add(selection.globalFilterName);
			}

			if (selection.regionFilters) {
				Object.values(selection.regionFilters).forEach((f: any) => this.usedFilters.add(f));
			}

			if (selection.boundsSelections) {
				selection.boundsSelections
					.filter((bs: BoundsSelection) => bs.filterName)
					.forEach((bs: BoundsSelection) => this.usedFilters.add(bs.filterName!));
			}
		} catch (e) {
			// Fehler beim Laden - Filter können gelöscht werden
		}
		this.cdr.detectChanges();
	}

	isFilterInUse(filterName?: string): boolean {
		if (!filterName) return false;
		return this.usedFilters.has(filterName);
	}

	refreshUsedFilters(): void {
		this.loadUsedFilters();
	}

	getDeleteTooltip(): string {
		if (this.isFilterInUse(this.name)) {
			return 'Filter wird im Projekt verwendet und kann nicht gelöscht werden';
		}
		return 'Filter löschen';
	}

	private updateFilters(filters: string[], reset = true) {
		this.filters = filters;
		if (!reset) return;
		if (filters.length > 0) {
			const recentFilter = this.storage.getSync('currentFilter');
			if (recentFilter && filters.indexOf(recentFilter) !== -1) {
				this.name = recentFilter;
			}
			else {
				this.name = filters.at(0);
			}
			this.fs.get(this.name!).then((c?: any) => this.conditions = c);
		}
		else {
			delete this.name;
			delete this.conditions;
		}
	}

	updateCondition() {
		this.fs.get(this.name!).then((c?: any) => this.conditions = c);
	}

	canAddNewFilter() {
		if (!this.newFilterName) return false;
		if (this.newFilterName?.length === 0) return false;
		if (!this.filters) return false;
		return this.filters.indexOf(this.newFilterName) === -1;
	}

	hasFilters() {
		return !!this.filters && this.filters.length > 0;
	}

	addNewFilter() {
		this.name = this.newFilterName;
		this.conditions = new ConditionExpression(ExpressionType.AND, new Array<Condition>);
		this.fs.set(this.name!, this.conditions).then(() => {
			this.fs.list_keys().then((filters: string[]) => this.updateFilters(filters, false));
		});
		this.storage.setSync('currentFilter', this.name!);
		delete this.newFilterName;
	}

	addNewFilterIfValid() {
		if (this.canAddNewFilter()) {
			this.addNewFilter();
		}
	}

	deleteFilter() {
		this.fs.remove(this.name!).then(() => {
			this.fs.list_keys().then((filters: string[]) => this.updateFilters(filters));
			this.fs.resetFilter();
		});
		delete this.newFilterName;
	}

	addNewCondition() {
		if (!this.conditions) return;
		this.newCondition.args = [...this.newArgs];
		this.conditions.conditions.push(Condition.condition(this.newCondition));
		this.fs.set(this.name!, this.conditions).then(() => {
			this.fs.list_keys().then((filters: string[]) => this.updateFilters(filters, false));
		});
		this.newCondition = Condition.emptyCondition();
		this.newArgs = [];
	}

	deleteCondition(i: number) {
		if (!this.conditions) return;
		this.conditions.conditions.splice(i, 1);
		this.fs.set(this.name!, this.conditions);
		this.newCondition = Condition.emptyCondition();
	}

	opString(op: any) {
		return decodeHTML(op.text);
	}

	onOperatorChanged() {
		if (!this.newCondition.value || !this.newCondition.opName) {
			this.newArgs = [];
			return;
		}
		const valueType = this.Values[this.newCondition.value]?.type;
		const op = Operators[valueType]?.[this.newCondition.opName];
		const operands = op?.operands ?? 0;
		while (this.newArgs.length < operands) {
			this.newArgs.push('');
		}
		while (this.newArgs.length > operands) {
			this.newArgs.pop();
		}
	}

	onValueChanged() {
		this.newCondition.opName = '';
		this.newArgs = [];
	}

	canAddNewCondition() {
		if (!this.newCondition.value || !this.newCondition.opName) return false;
		const valueType = this.Values[this.newCondition.value]?.type;
		const op = Operators[valueType]?.[this.newCondition.opName];
		const operands = op?.operands ?? 0;
		if (operands === 0) return true;
		return this.newArgs.length === operands && this.newArgs.every((a: any) => a && a.length > 0);
	}

	argumentChanged(data: string, conditionIndex: number, argIndex: number) {
		if (!this.conditions) return;
		this.conditions.conditions[conditionIndex].args[argIndex] = data;
	}

	saveConditions() {
		if (!this.conditions) return;
		this.fs.set(this.name!, this.conditions);
	}

	operatorChangedForCondition(conditionIndex: number, newOpName: string) {
		if (!this.conditions) return;
		const condition = this.conditions.conditions[conditionIndex];
		condition.setOperator(newOpName);
		this.fs.set(this.name!, this.conditions);
	}

	fieldChangedForCondition(conditionIndex: number, newFieldName: string) {
		if (!this.conditions) return;
		const condition = this.conditions.conditions[conditionIndex];
		condition.setField(newFieldName);
		this.fs.set(this.name!, this.conditions);
	}

	trackByConditionIndex(index: number, condition: Condition): number {
		return index;
	}

	trackByArgIndex(index: number, arg: any): string {
		return `arg-${index}`;
	}

	trackByIndex(index: number): number {
		return index;
	}

	filterChanged(data: string) {
		this.updateCondition();
		this.loadUsedFilters();
	}

	typeChanged(data: string) {
		if (!this.conditions) return;
		this.fs.set(this.name!, this.conditions);
	}

	canApply() {
		if (!this.conditions || !this.conditions.conditions || this.conditions.conditions.length === 0) return false;
		for (const c of this.conditions.conditions) {
			for (const a of c.args) {
				if (!a) return false;
			}
		}
		return true;
	}

	applyFilter() {
		this.storage.setSync('currentFilter', this.name!);
		if (!this.conditions) return;
		this.filterApplied.emit(this.name!);
	}

	onClose() {
		this.activeModal.close();
	}

	setScope(newScope: FilterScope) {
		if (this.scope === newScope) return;
		this.scope = newScope;
		this.scopeChanged.emit(newScope);
	}
}
